import React, { useContext } from 'react'
import DoughnutChart from '../../../../components/DoughnutChart'
import { AppContext } from '../../../../contexts/AppContext'

interface CarChargeDoughnutProps {
	hasTimeEstimate: boolean
	batteryLevel: number
	drivingRange: number
}

const CarChargeDoughnut: React.FC<CarChargeDoughnutProps> = ({
	hasTimeEstimate,
	batteryLevel,
	drivingRange,
}) => {
	const chargeLimit = 80 // Hardlimit for battery charging
	const { isDarkMode } = useContext(AppContext)

	const safetyMargin = 20

	const getChargedBelowSafety = (batteryLevel: number, safetyMargin: number) => {
		return batteryLevel < safetyMargin ? batteryLevel : 0
	}

	const getToBeChargedToSafety = (batteryLevel: number, safetyMargin: number) => {
		return batteryLevel < safetyMargin ? safetyMargin - batteryLevel : 0
	}

	const getChargedSafetyMargin = (batteryLevel: number, safetyMargin: number) => {
		return batteryLevel >= safetyMargin ? safetyMargin : 0
	}

	const getChargedAboveSafety = (batteryLevel: number, safetyMargin: number) => {
		return batteryLevel > safetyMargin ? batteryLevel - safetyMargin : 0
	}

	const getToBeChargedToLimit = (
		hasTimeEstimate: boolean,
		chargeLimit: number,
		batteryLevel: number,
		safetyMargin: number
	) => {
		return hasTimeEstimate && chargeLimit > Math.max(batteryLevel, safetyMargin)
			? chargeLimit - Math.max(batteryLevel, safetyMargin)
			: 0
	}

	const chargedBelowSafety = getChargedBelowSafety(batteryLevel, safetyMargin)
	const toBeChargedToSafety = getToBeChargedToSafety(batteryLevel, safetyMargin)
	const chargedSafetyMargin = getChargedSafetyMargin(batteryLevel, safetyMargin)
	const chargedAboveSafety = getChargedAboveSafety(batteryLevel, safetyMargin)
	const toBeChargedToLimit = getToBeChargedToLimit(
		hasTimeEstimate,
		chargeLimit,
		batteryLevel,
		safetyMargin
	)

	const total =
		chargedBelowSafety +
		toBeChargedToSafety +
		chargedSafetyMargin +
		chargedAboveSafety +
		toBeChargedToLimit
	const emptyCapacity = Math.max(100 - total, 0)

	const chartData = [
		chargedBelowSafety || chargedSafetyMargin,
		toBeChargedToSafety,
		chargedAboveSafety,
		toBeChargedToLimit,
		emptyCapacity,
	]

	const colors = isDarkMode
		? ['#3cb49f', '#ffc13e', '#3cb49f', '#ffc13e', '#ffffff']
		: ['#52ae32', '#ffc13e', '#52ae32', '#ffc13e', '#ffffff']

	return (
		<div className='doughnut-container'>
			<div className='labels'>
				<div className='font-bold mt-6 text-5xl text-text-dark dark:text-white'>
					{batteryLevel.toFixed(0)} <span className='text-4xl'>%</span>
				</div>
				<div className='text-lg mt-4 text-text-grey-200 dark:text-text-grey-100'>
					{drivingRange.toFixed(0)} km
				</div>
			</div>

			<DoughnutChart data={chartData} backgroundColors={colors} darkModeEnabled={isDarkMode} />
		</div>
	)
}

export default CarChargeDoughnut
